import {ColumnDef} from '@tanstack/react-table';
import {Organization} from '@tinct/core-api-models';
import {Avatar, AvatarFallback, Badge, BadgeStatus, TinctBadge} from '@tinct/web-components';
import {CollectionColumnHeader, CollectionRowActions} from '@tinct/web-collection';
import {format} from 'date-fns';
import {Building2, GalleryVerticalEnd, Pen, Settings, Trash} from 'lucide-react';

type UseUserOrganizationsColumnsOptions = {
  onManageWorkspace?: (organization: Organization) => void;
  onRenameWorkspace?: (organization: Organization) => void;
  onDeleteWorkspace?: (organization: Organization) => void;
};

export function useUserOrganizationsColumns(
  options: UseUserOrganizationsColumnsOptions = {},
): ColumnDef<Organization>[] {
  const { onManageWorkspace, onRenameWorkspace, onDeleteWorkspace } = options;

  return [
    {
      id: 'name',
      accessorKey: 'name',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Workspace" />
      ),
      cell: ({ row }) => {
        const { name, type, parent } = row.original;
        const isAgency = type === 'agency';

        return (
          <div className="flex items-center gap-2">
            <Avatar className="size-6 rounded-sm">
              <AvatarFallback className="rounded-sm text-xs">
                {isAgency ? <Building2 className="size-3" /> : <GalleryVerticalEnd className="size-3" />}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm font-semibold">{name ?? 'Unnamed'}</span>
            {isAgency && <Badge variant="outline">Agency</Badge>}
            {parent && (
              <Badge variant="secondary" title={parent.name ? `Managed by ${parent.name}` : undefined}>
                {parent.name ? `via ${parent.name}` : 'Managed'}
              </Badge>
            )}
          </div>
        );
      },
      meta: { className: 'min-w-[200px]' },
      enableSorting: true,
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Created" />
      ),
      cell: ({ row }) => (
        <div className="text-sm">
          {row.getValue('createdAt')
            ? format(row.getValue('createdAt'), 'dd/MM/yyyy')
            : '-'}
        </div>
      ),
      enableSorting: true,
    },
    {
      id: 'users',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Users" />
      ),
      cell: ({ row }) => {
        const count = row.original.statistics?.activeMembersCount ?? 0;
        return <span className="text-sm">{count}</span>;
      },
    },
    {
      id: 'campaigns',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Campaigns" />
      ),
      cell: ({ row }) => {
        if (row.original.type === 'agency') {
          return <span className="text-sm text-muted-foreground">-</span>;
        }
        const activeCount = row.original.statistics?.publishedCampaignsCount ?? 0;
        const totalCount = row.original.campaigns?.length ?? 0;
        return (
          <span className="text-sm">
            {activeCount} active / {totalCount}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: () => <span>Status</span>,
      cell: ({ row }) => {
        const deleteAt = row.original.deleteAt;
        const status = row.original.status;
        const hasSnippetActive = row.original.snippetStatus?.status === 'active';


        if (status === 'deleted') {
          return <BadgeStatus variant="red">Deleted</BadgeStatus>
        }

        if (deleteAt) {
          return <BadgeStatus variant="red">Deletion pending</BadgeStatus>
        }

        // Agencies have no snippet to install.
        if (!hasSnippetActive && row.original.type !== 'agency') {
          return <BadgeStatus variant="yellow">Setup needed</BadgeStatus>
        }

        return <BadgeStatus variant="green">Active</BadgeStatus>
      },
    },
    {
      id: 'plan',
      header: ({column}) => (
        <CollectionColumnHeader column={column} title="Plan" />
      ),
      cell: ({row}) => {
        const subscription = row.original.subscription;
        const planName = subscription
          ? (subscription.productGroup?.name ?? 'Pro')
          : 'Free';
        const isPro = subscription !== null;

        // Subscription served through the agency fallback: it belongs to another
        // organization (the managing agency), whose name is the only sub-field exposed.
        const isCoveredByOtherOrganization =
          subscription?.organizationId != null &&
          subscription.organizationId !== row.original.id;

        // The subscribed product(s) and price(s), when the page requested
        // subscription items.
        const productLabels = [
          ...new Set(
            (subscription?.items ?? [])
              .map((item) => {
                const productName = item.price?.product?.name;
                const priceName = item.price?.nickname;
                if (productName && priceName) return `${productName} — ${priceName}`;
                return productName ?? priceName ?? '';
              })
              .filter((label) => label !== ''),
          ),
        ];

        return (
          <div className="flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center gap-1.5">
              {isPro ? (
                <div className="inline-block"><TinctBadge label={planName} /></div>
              ) : (
                <Badge variant="secondary">{planName}</Badge>
              )}
              {isCoveredByOtherOrganization && (
                <Badge variant="outline">
                  via {subscription.organization?.name ?? 'agency'}
                </Badge>
              )}
            </div>
            {productLabels.length > 0 && (
              <span className="text-xs text-muted-foreground truncate">
                {productLabels.join(', ')}
              </span>
            )}
          </div>
        );
      },
      enableSorting: true,
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        return (
          <div className="flex items-center justify-end gap-1">
            <CollectionRowActions
              row={row}
              actions={[
                {
                  label: 'Manage workspace',
                  icon: Settings,
                  onClick: (organization: Organization) => {
                    onManageWorkspace?.(organization);
                  },
                },
                {
                  label: 'Rename',
                  icon: Pen,
                  onClick: (organization: Organization) => {
                    onRenameWorkspace?.(organization);
                  },
                },
                {
                  label: 'Delete workspace',
                  icon: Trash,
                  group: 'danger',
                  className: 'text-red-500!',
                  onClick: (organization: Organization) => {
                    onDeleteWorkspace?.(organization);
                  },
                },
              ]}
            />
          </div>
        );
      },
    },
  ];
}
